import {
  useChatSessionDocumentProcessing,
  useChatSessionGroupDocumentProcessing
} from '@/composables/useDocumentProcessing'
import {
  useOrganizationDocumentProcessing,
  type OrganizationDocumentMessage
} from '@/composables/useOrganizationDocumentProcessing'
import { useUserStore } from '@/shared/stores/userStore'
import type {
  AiChatQuestionTemplate,
  AIChatSession,
  CustomerOrganisationUser,
  SessionGroup,
  TUser
} from '@/types/generated'
import {
  AiChatQuestionTemplatesService,
  AiChatSessionGroupsService,
  AiChatSessionsService,
  AIChatSessionTypeEnum,
  UsersService
} from '@/types/generated'
import { defineStore } from 'pinia'
import { computed, readonly, ref, watch } from 'vue'

/**
 * Same key as ``AiChatSidebar`` localStorage — clearing it prevents auto-resuming the last
 * thread when entering fullscreen global chat via the top nav.
 */
export const GLOBAL_AI_CHAT_LAST_SELECTED_SESSION_STORAGE_KEY = 'ai-chat-last-selected-chat'

/**
 * Mirror :data:`PAGE_SIZE` in :file:`ChatHistoryModal.vue`. Pinned to the backend
 * default (``DEFAULT_PAGINATION_CLASS`` / ``PAGE_SIZE = 20``). Both the chat list
 * and the folder list use the same window so the scroll-to-load UX is consistent
 * across the AI Chat sidebar.
 */
const SIDEBAR_PAGE_SIZE = 20

export const useGlobalChatStore = defineStore('globalChat', () => {
  // State
  const sessionGroups = ref<SessionGroup[]>([])
  const activeSessionGroup = ref<SessionGroup | null>(null)
  const chatSessions = ref<AIChatSession[]>([])
  const activeChatSession = ref<AIChatSession | null>(null)
  const promptTemplates = ref<AiChatQuestionTemplate[]>([])
  const organizationUsers = ref<TUser[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Pagination state for the AI Chat sidebar lists. The store keeps a single
  // pagination cursor for ``chatSessions`` and another for ``sessionGroups``;
  // the AI Chat sidebar consumes both via ``loadMoreChatSessions`` /
  // ``loadMoreSessionGroups`` on viewport-bottom scroll. Server-side search is
  // captured in the same cursor so paginating preserves the active filter.
  const chatSessionsLoadingMore = ref(false)
  const chatSessionsHasMore = ref(false)
  const sessionGroupsLoadingMore = ref(false)
  const sessionGroupsHasMore = ref(false)

  /**
   * Active filter snapshot for the chat-sessions list. Captures the parameters
   * of the most recent reset-load so :func:`loadMoreChatSessions` can paginate
   * with the same filter without callers having to re-supply it. ``null``
   * means no list has been loaded yet (initial state); refusing to paginate in
   * that case avoids an unfiltered append onto a stale snapshot.
   */
  let chatSessionsCurrentFilter: {
    sessionGroup?: string
    search?: string
  } | null = null
  let chatSessionsFetchId = 0

  /**
   * Active filter snapshot for the session-groups list. Same contract as
   * :data:`chatSessionsCurrentFilter` — see that comment for the pagination
   * pre-condition.
   */
  let sessionGroupsCurrentFilter: { search?: string } | null = null
  let sessionGroupsFetchId = 0

  /**
   * When true, global fullscreen chat should show the welcome / empty state and must not
   * auto-select the first thread or restore ``GLOBAL_AI_CHAT_LAST_SELECTED_SESSION_STORAGE_KEY``.
   */
  const preferEmptyGlobalFullscreenSession = ref(false)

  // Document processors for global chat - each session gets its own processor
  const sessionProcessors = ref<Map<string, any>>(new Map())
  const folderProcessors = ref<Map<string, any>>(new Map())

  // Document registry: maps document UUID to its owner (session/folder)
  // Format: { documentUuid: { sessionUuid?, folderUuid?, uploadTimestamp } }
  interface DocumentRegistryEntry {
    sessionUuid?: string
    folderUuid?: string
    uploadTimestamp: number
  }
  const documentRegistry = ref<Map<string, DocumentRegistryEntry>>(new Map())

  // Global websocket connection for organization documents
  const userStore = useUserStore()
  const organizationUuid = computed(() => userStore.user?.org?.uuid)

  // Handler for websocket messages - routes to correct processor
  const handleGlobalDocumentUpdate = (message: OrganizationDocumentMessage) => {
    const { document_uuid, status, error } = message

    // Look up document in registry
    const registryEntry = documentRegistry.value.get(document_uuid)

    if (!registryEntry) {
      // Fallback: try to find document in any processor
      // This handles cases where document was uploaded but not registered yet
      let found = false

      // Check session processors
      for (const [sessionUuid, processor] of sessionProcessors.value.entries()) {
        const doc = processor?.documents?.value?.find((d: any) => d.uuid === document_uuid)
        if (doc) {
          processor.updateDocumentStatusByMessage?.(document_uuid, status, error)
          found = true
          // Register it for future updates
          registerDocument(document_uuid, { sessionUuid })
          break
        }
      }

      // Check folder processors if not found
      if (!found) {
        for (const [folderUuid, processor] of folderProcessors.value.entries()) {
          const doc = processor?.documents?.value?.find((d: any) => d.uuid === document_uuid)
          if (doc) {
            processor.updateDocumentStatusByMessage?.(document_uuid, status, error)
            found = true
            // Register it for future updates
            registerDocument(document_uuid, { folderUuid })
            break
          }
        }
      }
      return
    }

    // Route to correct processor(s) based on registry entry
    const processorsToUpdate: any[] = []

    if (registryEntry.sessionUuid) {
      const processor = sessionProcessors.value.get(registryEntry.sessionUuid)
      if (processor) {
        processorsToUpdate.push({ processor, type: 'session', uuid: registryEntry.sessionUuid })
      }
    }

    if (registryEntry.folderUuid) {
      const processor = folderProcessors.value.get(registryEntry.folderUuid)
      if (processor) {
        processorsToUpdate.push({ processor, type: 'folder', uuid: registryEntry.folderUuid })
      }
    }

    // Update all relevant processors
    processorsToUpdate.forEach(({ processor }) => {
      processor.updateDocumentStatusByMessage?.(document_uuid, status, error)
    })
  }

  // Global websocket connection
  const globalWebsocket = useOrganizationDocumentProcessing(handleGlobalDocumentUpdate)

  // Initialize websocket when organization UUID is available
  watch(
    organizationUuid,
    (newOrgUuid) => {
      if (newOrgUuid && !globalWebsocket.isSubscribed.value) {
        globalWebsocket.startSubscription()
      }
    },
    { immediate: true }
  )

  // Register a document in the registry
  const registerDocument = (
    documentUuid: string,
    owner: { sessionUuid?: string; folderUuid?: string }
  ) => {
    documentRegistry.value.set(documentUuid, {
      sessionUuid: owner.sessionUuid,
      folderUuid: owner.folderUuid,
      uploadTimestamp: Date.now()
    })
  }

  // Unregister a document from the registry
  const unregisterDocument = (documentUuid: string) => {
    documentRegistry.value.delete(documentUuid)
  }

  // Get or create processor for a specific session
  const getSessionProcessor = (sessionUuid: string) => {
    if (!sessionProcessors.value.has(sessionUuid)) {
      // Resolve the session from ``chatSessions`` first (sidebar-loaded state) and
      // fall back to ``activeChatSession`` so that sessions restored via the
      // fullscreen history popover — which bypass the store list — still yield a
      // valid ``uploadTarget`` and don't break document uploads.
      const sessionRef = computed(() => {
        const fromList = chatSessions.value.find((s) => s.uuid === sessionUuid)
        if (fromList) return fromList
        if (activeChatSession.value?.uuid === sessionUuid) {
          return activeChatSession.value
        }
        return null
      })
      const processor = useChatSessionDocumentProcessing(sessionRef)

      // Set registration callback so documents are registered in global registry
      // The owner already includes sessionUuid from the processor, but we ensure it's set
      processor.setDocumentRegistrationCallback((documentUuid, owner) => {
        const session = sessionRef.value
        registerDocument(documentUuid, {
          sessionUuid: sessionUuid,
          folderUuid: session?.session_group || owner.folderUuid
        })
      })

      sessionProcessors.value.set(sessionUuid, processor)
    }
    return sessionProcessors.value.get(sessionUuid)
  }

  // Get or create processor for a specific folder
  const getFolderProcessor = (folderUuid: string) => {
    if (!folderProcessors.value.has(folderUuid)) {
      const groupRef = computed(
        () => sessionGroups.value.find((f) => f.uuid === folderUuid) || null
      )
      const processor = useChatSessionGroupDocumentProcessing(groupRef)

      // Set registration callback so documents are registered in global registry
      processor.setDocumentRegistrationCallback((documentUuid, owner) => {
        registerDocument(documentUuid, {
          folderUuid: folderUuid,
          sessionUuid: owner.sessionUuid // May be undefined if uploaded directly to folder
        })
      })

      folderProcessors.value.set(folderUuid, processor)
    }
    return folderProcessors.value.get(folderUuid)
  }

  // Legacy processors for backward compatibility
  const chatSessionProcessor = computed(() => {
    const session = activeChatSession.value
    return session?.uuid ? getSessionProcessor(session.uuid) : null
  })

  const folderProcessor = computed(() => {
    const group = activeSessionGroup.value
    return group?.uuid ? getFolderProcessor(group.uuid) : null
  })

  // Getters
  const hasSessionGroups = computed(() => sessionGroups.value.length > 0)
  const hasChatSessions = computed(() => chatSessions.value.length > 0)

  /**
   * Page-fetch the global chat sessions list. Mutates ``chatSessions`` either
   * by replacing it (``append=false``, used for fresh loads / filter changes)
   * or by extending it (``append=true``, used for scroll-driven pagination).
   *
   * A monotonically-increasing fetch id (:data:`chatSessionsFetchId`) guards
   * against races: rapid filter changes (folder switch + search keystroke,
   * etc.) discard any late response that is no longer the current request, so
   * the UI never flashes stale rows in. Same pattern as
   * :func:`fetchSessions` in :file:`ChatHistoryModal.vue`.
   *
   * :param filter: Filter snapshot to apply (``sessionGroup`` UUID and / or
   *     ``search`` query). Stored on :data:`chatSessionsCurrentFilter` for
   *     follow-up :func:`loadMoreChatSessions` calls.
   * :param append: ``true`` to append the next page; ``false`` to reset and
   *     reload from offset 0.
   * :return: ``None``
   */
  const fetchChatSessionsPage = async (
    filter: { sessionGroup?: string; search?: string },
    append: boolean
  ) => {
    const fetchId = ++chatSessionsFetchId
    if (append) {
      chatSessionsLoadingMore.value = true
    } else {
      loading.value = true
      chatSessions.value = []
      chatSessionsHasMore.value = true
      // Clear any leftover load-more flag from an in-flight append we just
      // invalidated by bumping ``chatSessionsFetchId``. The losing append's
      // ``finally`` block is gated on ``fetchId === chatSessionsFetchId`` and
      // therefore can't reset its own flag once it loses the race — without
      // this line the spinner row stays visible forever and every subsequent
      // ``loadMoreChatSessions`` no-ops because the load-more guard sees a
      // perpetually-true flag.
      chatSessionsLoadingMore.value = false
      chatSessionsCurrentFilter = { ...filter }
    }
    try {
      error.value = null
      const response = await AiChatSessionsService.aiChatSessionsList({
        sessionType: AIChatSessionTypeEnum.GLOBAL,
        sessionGroup: filter.sessionGroup,
        search: filter.search?.trim() ? filter.search.trim() : undefined,
        limit: SIDEBAR_PAGE_SIZE,
        offset: append ? chatSessions.value.length : 0
      })
      if (fetchId !== chatSessionsFetchId) return
      const results = response.results ?? []
      chatSessions.value = append ? [...chatSessions.value, ...results] : results
      chatSessionsHasMore.value = results.length >= SIDEBAR_PAGE_SIZE
    } catch (err) {
      // Only the reset path writes to ``error.value``: that ref is consumed by
      // ``AiAssistantChatView`` to render a full-page fatal banner with a
      // "retry initial load" button, so a transient append (scroll-load)
      // failure must not throw the user out of their list. Append failures
      // are logged + leave the user's existing rows on screen; the next
      // scroll tick can retry.
      if (fetchId === chatSessionsFetchId && !append) {
        error.value = 'Failed to load global chat sessions'
      }
      console.error('Error loading global chat sessions:', err)
    } finally {
      if (fetchId === chatSessionsFetchId) {
        loading.value = false
        chatSessionsLoadingMore.value = false
      }
    }
  }

  const fetchSessionGroupsPage = async (filter: { search?: string }, append: boolean) => {
    const fetchId = ++sessionGroupsFetchId
    if (append) {
      sessionGroupsLoadingMore.value = true
    } else {
      loading.value = true
      sessionGroups.value = []
      sessionGroupsHasMore.value = true
      // Same race-cleanup as in :func:`fetchChatSessionsPage` — see that
      // comment for why the leftover flag has to be cleared here.
      sessionGroupsLoadingMore.value = false
      sessionGroupsCurrentFilter = { ...filter }
    }
    try {
      error.value = null
      const response = await AiChatSessionGroupsService.aiChatSessionGroupsList({
        search: filter.search?.trim() ? filter.search.trim() : undefined,
        limit: SIDEBAR_PAGE_SIZE,
        offset: append ? sessionGroups.value.length : 0
      })
      if (fetchId !== sessionGroupsFetchId) return
      const results = response.results ?? []
      sessionGroups.value = append ? [...sessionGroups.value, ...results] : results
      sessionGroupsHasMore.value = results.length >= SIDEBAR_PAGE_SIZE
    } catch (err) {
      // Same error contract as :func:`fetchChatSessionsPage` — append
      // failures stay silent so a transient scroll-load 5xx doesn't
      // re-route the user through the fatal-error recovery banner.
      if (fetchId === sessionGroupsFetchId && !append) {
        error.value = 'Failed to load session groups'
      }
      console.error('Error loading session groups:', err)
    } finally {
      if (fetchId === sessionGroupsFetchId) {
        loading.value = false
        sessionGroupsLoadingMore.value = false
      }
    }
  }

  // Actions
  /**
   * Load the first page of the folders (session groups) list. Resets any
   * pagination cursor and applies ``search`` server-side.
   *
   * :param search: Optional server-side ``search`` filter forwarded to the
   *     ``aiChatSessionGroupsList`` endpoint. Empty / whitespace strings are
   *     dropped before being sent.
   * :return: ``None``
   */
  const loadSessionGroups = async (search?: string) => {
    await fetchSessionGroupsPage({ search }, false)
  }

  /**
   * Append the next page of folders using the most-recent filter snapshot.
   *
   * No-op when no fresh load has happened yet, when a load-more is already
   * in flight, or when the previous page was a partial (i.e. we already
   * reached the end).
   *
   * :return: ``None``
   */
  const loadMoreSessionGroups = async () => {
    if (
      !sessionGroupsCurrentFilter ||
      sessionGroupsLoadingMore.value ||
      !sessionGroupsHasMore.value
    ) {
      return
    }
    await fetchSessionGroupsPage(sessionGroupsCurrentFilter, true)
  }

  /**
   * Load the first page of the standalone global chat list (i.e. no folder
   * filter). Resets pagination and applies ``search`` server-side.
   *
   * :param search: Optional server-side ``search`` filter forwarded to the
   *     ``aiChatSessionsList`` endpoint. Empty / whitespace strings are
   *     dropped before being sent.
   * :return: ``None``
   */
  const loadStandaloneGlobalChatSessions = async (search?: string) => {
    await fetchChatSessionsPage({ search }, false)
  }

  /**
   * Append the next page of chat sessions using the most-recent filter
   * snapshot.
   *
   * Same no-op rules as :func:`loadMoreSessionGroups`.
   *
   * :return: ``None``
   */
  const loadMoreChatSessions = async () => {
    if (!chatSessionsCurrentFilter || chatSessionsLoadingMore.value || !chatSessionsHasMore.value) {
      return
    }
    await fetchChatSessionsPage(chatSessionsCurrentFilter, true)
  }

  const loadGlobalPromptTemplates = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await AiChatQuestionTemplatesService.aiChatQuestionTemplatesList({
        limit: 0
      })
      promptTemplates.value = response.results
    } catch (err) {
      error.value = 'Failed to load global prompt templates'
      console.error('Error loading global prompt templates:', err)
    } finally {
      loading.value = false
    }
  }

  const loadOrganizationUsers = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await UsersService.usersList({
        isActive: true,
        limit: 100 // Get all users in the organization
      })
      organizationUsers.value = response.results
    } catch (err) {
      error.value = 'Failed to load organization users'
      console.error('Error loading organization users:', err)
    } finally {
      loading.value = false
    }
  }

  const loadSessionGroup = async (uuid: string) => {
    try {
      loading.value = true
      error.value = null
      const sessionGroup = await AiChatSessionGroupsService.aiChatSessionGroupsRetrieve({ uuid })
      activeSessionGroup.value = sessionGroup

      // Load chat sessions for this group
      await loadChatSessions(uuid)
    } catch (err) {
      error.value = 'Failed to load session group'
      console.error('Error loading session group:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Load the first page of chat sessions for a specific folder. Resets the
   * pagination cursor and pins the session-group filter so subsequent
   * :func:`loadMoreChatSessions` calls keep paginating within the same folder.
   *
   * :param sessionGroupUuid: UUID of the folder to scope the listing to.
   * :param search: Optional server-side ``search`` filter; same trim
   *     semantics as :func:`loadStandaloneGlobalChatSessions`.
   * :return: ``None``
   */
  const loadChatSessions = async (sessionGroupUuid: string, search?: string) => {
    await fetchChatSessionsPage({ sessionGroup: sessionGroupUuid, search }, false)
  }

  const setActiveSessionGroupFromChatSession = async (chatSession: AIChatSession) => {
    if (!chatSession.session_group) {
      activeSessionGroup.value = null
      return
    }

    // Check if we already have the session group loaded
    const existingGroup = sessionGroups.value.find(
      (group) => group.uuid === chatSession.session_group
    )
    if (existingGroup) {
      activeSessionGroup.value = existingGroup
      return
    }

    // Load the session group if we don't have it
    try {
      const sessionGroup = await AiChatSessionGroupsService.aiChatSessionGroupsRetrieve({
        uuid: chatSession.session_group
      })
      activeSessionGroup.value = sessionGroup
    } catch (err) {
      console.error('Failed to load session group for chat session:', err)
      activeSessionGroup.value = null
    }
  }

  const createChatSession = async (
    sessionGroupUuid: string,
    title?: string,
    description?: string
  ) => {
    try {
      loading.value = true
      error.value = null

      const session = await AiChatSessionsService.aiChatSessionsCreate({
        requestBody: {
          session_group_uuid: sessionGroupUuid,
          title: title || 'New Chat',
          description: description || '',
          session_type: AIChatSessionTypeEnum.GLOBAL
        }
      })

      chatSessions.value.unshift(session)
      activeChatSession.value = session

      return session
    } catch (err) {
      console.error('Error creating chat session:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const setActiveChatSession = (session: AIChatSession | null) => {
    if (session && preferEmptyGlobalFullscreenSession.value) {
      preferEmptyGlobalFullscreenSession.value = false
    }
    activeChatSession.value = session
    // Processor will be created on-demand when accessed via getSessionProcessor
  }

  /**
   * Reset global fullscreen chat to a fresh welcome state (no active thread, no sidebar restore).
   *
   * :return: None
   */
  const prepareFreshFullscreenGlobalEntry = () => {
    preferEmptyGlobalFullscreenSession.value = true
    activeChatSession.value = null
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(GLOBAL_AI_CHAT_LAST_SELECTED_SESSION_STORAGE_KEY)
      }
    } catch {
      // Ignore storage failures (private mode, disabled storage).
    }
  }

  const updateChatSession = (updatedSession: AIChatSession) => {
    const index = chatSessions.value.findIndex((s) => s.uuid === updatedSession.uuid)

    if (index !== -1) {
      chatSessions.value[index] = updatedSession
    }

    if (activeChatSession.value?.uuid === updatedSession.uuid) {
      activeChatSession.value = updatedSession
    }
  }

  const clearError = () => {
    error.value = null
  }

  const getUserByUuid = (uuid: string) => {
    // First try to find user data from chat sessions (more reliable)
    for (const session of chatSessions.value) {
      const userData = (session as any).started_by_user_data
      if (userData && userData.uuid === uuid) {
        return userData
      }
    }

    // Fallback to organization users
    const user = organizationUsers.value.find((user) => user.uuid === uuid) || null
    return user
  }

  const getOrganisationUserByUuid = (uuid: string): CustomerOrganisationUser | null => {
    // First try to find user data from chat sessions (more reliable)
    for (const session of chatSessions.value) {
      const userData = (session as any).started_by_user_data
      if (userData && userData.uuid === uuid) {
        // Map to CustomerOrganisationUser format
        return {
          uuid: userData.uuid,
          email: userData.email,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          avatar: userData.avatar || null
        }
      }
    }

    // Fallback to organization users
    const user = organizationUsers.value.find((user) => user.uuid === uuid)
    if (user && user.email && user.uuid) {
      return {
        uuid: user.uuid,
        email: user.email,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        avatar: user.avatar || null
      }
    }

    return null
  }

  // Session Group Management
  const createSessionGroup = async (data: {
    name: string
    description?: string
    instructions?: string
  }) => {
    try {
      loading.value = true
      error.value = null
      const response = await AiChatSessionGroupsService.aiChatSessionGroupsCreate({
        requestBody: {
          name: data.name,
          description: data.description,
          instructions: data.instructions
        }
      })
      sessionGroups.value.unshift(response)
      return response
    } catch (err) {
      console.error('Error creating session group:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateSessionGroup = async (
    uuid: string,
    data: { name: string; description?: string; instructions?: string }
  ) => {
    try {
      loading.value = true
      error.value = null
      // Use PATCH so callers can send only the fields they edit; do not set
      // ``error`` on failure — interactive callers handle errors locally. Setting
      // ``error`` here would make ``AiAssistantChatView`` treat any rename
      // failure as a full-page fatal load state.
      const response = await AiChatSessionGroupsService.aiChatSessionGroupsPartialUpdate({
        uuid,
        requestBody: {
          name: data.name,
          description: data.description,
          instructions: data.instructions
        }
      })
      const index = sessionGroups.value.findIndex((group) => group.uuid === uuid)
      if (index !== -1) {
        sessionGroups.value[index] = response
      }
      return response
    } catch (err) {
      console.error('Error updating session group:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const refreshSessionGroup = async (uuid: string) => {
    try {
      loading.value = true
      error.value = null
      const response = await AiChatSessionGroupsService.aiChatSessionGroupsRetrieve({ uuid })
      const index = sessionGroups.value.findIndex((group) => group.uuid === uuid)
      if (index !== -1) {
        sessionGroups.value[index] = response
      }
      return response
    } catch (err) {
      console.error('Error refreshing session group:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteSessionGroup = async (uuid: string) => {
    try {
      loading.value = true
      error.value = null

      // Get chat sessions that belong to this folder before deletion
      const sessionsToDelete = chatSessions.value.filter(
        (session) => session.session_group === uuid
      )

      await AiChatSessionGroupsService.aiChatSessionGroupsDestroy({ uuid })

      // Remove the session group from the store
      sessionGroups.value = sessionGroups.value.filter((group) => group.uuid !== uuid)

      // Remove all chat sessions that belonged to this folder
      chatSessions.value = chatSessions.value.filter((session) => session.session_group !== uuid)

      // Clean up processors for all deleted sessions
      sessionsToDelete.forEach((session) => {
        if (sessionProcessors.value.has(session.uuid)) {
          // Clean up processor state
          const processor = sessionProcessors.value.get(session.uuid)
          processor?.clearDocumentProcessingStates?.()
          sessionProcessors.value.delete(session.uuid)
        }
        // Clean up document registry entries for this session
        for (const [docUuid, entry] of documentRegistry.value.entries()) {
          if (entry.sessionUuid === session.uuid) {
            documentRegistry.value.delete(docUuid)
          }
        }
      })

      // Clean up the folder processor
      if (folderProcessors.value.has(uuid)) {
        // Clean up processor state
        const processor = folderProcessors.value.get(uuid)
        processor?.clearDocumentProcessingStates?.()
        folderProcessors.value.delete(uuid)
      }

      // Clean up document registry entries for this folder
      for (const [docUuid, entry] of documentRegistry.value.entries()) {
        if (entry.folderUuid === uuid) {
          documentRegistry.value.delete(docUuid)
        }
      }

      // If the active chat session was in this folder, clear it
      if (activeChatSession.value?.session_group === uuid) {
        activeChatSession.value = null
      }

      // If this was the active session group, clear it
      if (activeSessionGroup.value?.uuid === uuid) {
        activeSessionGroup.value = null
      }
    } catch (err) {
      console.error('Error deleting session group:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Remove a chat session from in-memory state without calling the API. Mirrors
   * the per-session cleanup done by :func:`deleteChatSession` (session list,
   * processor map, document registry, active-session slot) and is intended for
   * callers that have already destroyed the session through another path
   * (e.g. the unified chat store) and just need the global store to catch up.
   *
   * :param uuid: UUID of the session that was deleted upstream.
   */
  const removeChatSession = (uuid: string) => {
    chatSessions.value = chatSessions.value.filter((session) => session.uuid !== uuid)
    if (sessionProcessors.value.has(uuid)) {
      const processor = sessionProcessors.value.get(uuid)
      processor?.clearDocumentProcessingStates?.()
      sessionProcessors.value.delete(uuid)
    }
    for (const [docUuid, entry] of documentRegistry.value.entries()) {
      if (entry.sessionUuid === uuid) {
        documentRegistry.value.delete(docUuid)
      }
    }
    if (activeChatSession.value?.uuid === uuid) {
      activeChatSession.value = null
    }
  }

  const deleteChatSession = async (uuid: string) => {
    try {
      loading.value = true
      error.value = null
      await AiChatSessionsService.aiChatSessionsDestroy({ uuid })
      removeChatSession(uuid)
    } catch (err) {
      console.error('Error deleting chat session:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    sessionGroups,
    activeSessionGroup,
    chatSessions,
    activeChatSession,
    promptTemplates,
    organizationUsers,
    loading,
    error,
    preferEmptyGlobalFullscreenSession: readonly(preferEmptyGlobalFullscreenSession),

    // Pagination state (sidebar load-on-scroll)
    chatSessionsLoadingMore: readonly(chatSessionsLoadingMore),
    chatSessionsHasMore: readonly(chatSessionsHasMore),
    sessionGroupsLoadingMore: readonly(sessionGroupsLoadingMore),
    sessionGroupsHasMore: readonly(sessionGroupsHasMore),

    // Getters
    hasSessionGroups,
    hasChatSessions,

    // Document Processors
    chatSessionProcessor,
    folderProcessor,
    getSessionProcessor,
    getFolderProcessor,

    // Actions
    loadSessionGroups,
    loadMoreSessionGroups,
    loadStandaloneGlobalChatSessions,
    loadMoreChatSessions,
    loadGlobalPromptTemplates,
    loadOrganizationUsers,
    loadSessionGroup,
    loadChatSessions,
    setActiveSessionGroupFromChatSession,
    createChatSession,
    setActiveChatSession,
    prepareFreshFullscreenGlobalEntry,
    updateChatSession,
    clearError,
    getUserByUuid,
    getOrganisationUserByUuid,

    // Session Group Management
    createSessionGroup,
    updateSessionGroup,
    refreshSessionGroup,
    deleteSessionGroup,

    // Chat Session Management
    deleteChatSession,
    removeChatSession,

    // Document Registry
    registerDocument,
    unregisterDocument,

    // Global Websocket
    globalWebsocket: {
      isConnected: globalWebsocket.isConnected,
      isConnecting: globalWebsocket.isConnecting,
      hasError: globalWebsocket.hasError,
      isSubscribed: globalWebsocket.isSubscribed
    }
  }
})
